import { MerkleClaim } from "../rest/kairos/types";

export function createMintToPayload(amountIn: number, minAmountOut: number): Buffer {
  const instructionByte = new Uint8Array([11]);

  const num1Bytes = new Uint8Array(new BigUint64Array([BigInt(amountIn)]).buffer);
  const num2Bytes = new Uint8Array(new BigUint64Array([BigInt(minAmountOut)]).buffer);

  const payloadBytes = new Uint8Array([...instructionByte, ...num1Bytes, ...num2Bytes]);

  return Buffer.from(payloadBytes);
}

export function createEnqueueWithdrawalPayload(amount: number): Buffer {
  const instructionByte = new Uint8Array([12]);

  const num1Bytes = new Uint8Array(new BigUint64Array([BigInt(amount)]).buffer);

  const payloadBytes = new Uint8Array([...instructionByte, ...num1Bytes]);

  return Buffer.from(payloadBytes);
}

export function createBurnWithdrawalPayload(amount: number): Buffer {
  const instructionByte = new Uint8Array([14]);

  const payloadBytes = new Uint8Array([...instructionByte]);

  return Buffer.from(payloadBytes);
}

export function updateVaultBalancePayload(): Buffer {
  const instructionByte = new Uint8Array([25]);

  const payloadBytes = new Uint8Array([...instructionByte]);

  return Buffer.from(payloadBytes);
}

export function depositSolPayload(depositAmount: number): Buffer {
  const instructionByte = new Uint8Array([14]);
  const num1Bytes = new Uint8Array(new BigUint64Array([BigInt(depositAmount)]).buffer);
  const payloadBytes = new Uint8Array([...instructionByte, ...num1Bytes]);
  return Buffer.from(payloadBytes);
}

export function createClaimRewardsPayload(merkleClaim: MerkleClaim): Buffer {
  const instructionBytes = new Uint8Array([78, 177, 98, 123, 210, 21, 187, 83]);
  const paddingBytes = new Uint8Array(new Uint8Array([13, 0, 0, 0]));

  const amountUnlockedBytes = new Uint8Array(new BigUint64Array([BigInt(merkleClaim.userProof.amount)]).buffer);
  const amountLockedBytes = new Uint8Array(new BigUint64Array([BigInt(0)]).buffer);

  // Flatten the proof array of arrays into a single Uint8Array
  const proofBytes = new Uint8Array(merkleClaim.userProof.proof.flat());

  const payloadBytes = new Uint8Array([
    ...instructionBytes,
    ...amountUnlockedBytes,
    ...amountLockedBytes,
    ...paddingBytes,
    ...proofBytes
  ]);

  return Buffer.from(payloadBytes);
}

// ---------------------------------------------------------------------------
// Drip
//
// The program exposes two instruction families with identical argument layouts:
// the legacy kySOL instructions and the generic asset-vault instructions. Only
// the 8-byte discriminator differs, so the encoders below are shared.
// Discriminators come from src/idl/drip_vault_v2.json.
// ---------------------------------------------------------------------------

const DRIP_DISCRIMINATORS = {
  deposit: [0xf2, 0x23, 0xc6, 0x89, 0x52, 0xe1, 0xf2, 0xb6],
  redeem: [0xb8, 0x0c, 0x56, 0x95, 0x46, 0xc4, 0x61, 0xe1],
  updateRewardToken: [0x11, 0x7a, 0x43, 0x88, 0x6e, 0xb0, 0xa1, 0xa7],
  depositAsset: [107, 93, 89, 87, 226, 203, 154, 19],
  redeemAsset: [209, 90, 40, 93, 58, 93, 100, 158],
  updateRewardTokenAsset: [2, 208, 137, 27, 184, 203, 209, 15],
} as const;

type DripPubkeyLike = { toBytes: () => Uint8Array };

const u64 = (value: number): Uint8Array => {
  // BigUint64Array coerces modulo 2^64 rather than range-checking, so a
  // negative or oversized value would silently encode as a wildly wrong
  // amount. These payloads move user funds; fail loudly instead.
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new RangeError(`Drip payload amount must be a non-negative safe integer, got ${value}`);
  }
  return new Uint8Array(new BigUint64Array([BigInt(value)]).buffer);
};

/** amount: u64, reward_token_mint: Option<Pubkey> */
const encodeDepositArgs = (
  discriminator: readonly number[],
  amount: number,
  rewardTokenMint: DripPubkeyLike | null
): Buffer => {
  const head = [...discriminator, ...u64(amount)];
  const tail = rewardTokenMint
    ? [1, ...rewardTokenMint.toBytes()] // Some
    : [0]; // None
  return Buffer.from(new Uint8Array([...head, ...tail]));
};

/** shares: u64 */
const encodeSharesArg = (discriminator: readonly number[], shares: number): Buffer =>
  Buffer.from(new Uint8Array([...discriminator, ...u64(shares)]));

/** mint: Pubkey */
const encodePubkeyArg = (
  discriminator: readonly number[],
  mint: DripPubkeyLike
): Buffer => Buffer.from(new Uint8Array([...discriminator, ...mint.toBytes()]));

// --- legacy kySOL vault ---

export function createDripDepositPayload(
  amount: number,
  rewardTokenMint: DripPubkeyLike | null
): Buffer {
  return encodeDepositArgs(DRIP_DISCRIMINATORS.deposit, amount, rewardTokenMint);
}

export function createDripRedeemPayload(shares: number): Buffer {
  return encodeSharesArg(DRIP_DISCRIMINATORS.redeem, shares);
}

export function createDripUpdateRewardTokenPayload(
  newRewardTokenMint: DripPubkeyLike
): Buffer {
  return encodePubkeyArg(DRIP_DISCRIMINATORS.updateRewardToken, newRewardTokenMint);
}

// --- generic asset vaults (hSOL and later) ---

export function createDripDepositAssetPayload(
  amount: number,
  rewardTokenMint: DripPubkeyLike | null
): Buffer {
  return encodeDepositArgs(DRIP_DISCRIMINATORS.depositAsset, amount, rewardTokenMint);
}

export function createDripRedeemAssetPayload(shares: number): Buffer {
  return encodeSharesArg(DRIP_DISCRIMINATORS.redeemAsset, shares);
}

export function createDripUpdateRewardTokenAssetPayload(
  newRewardTokenMint: DripPubkeyLike
): Buffer {
  return encodePubkeyArg(
    DRIP_DISCRIMINATORS.updateRewardTokenAsset,
    newRewardTokenMint
  );
}